import { Injectable, Injector } from "@angular/core";
import { collection, doc, Firestore, onSnapshot } from "@angular/fire/firestore";
import { AuthService } from "@core/auth.service";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { filter, map, shareReplay, switchMap, switchMapTo, take } from "rxjs/operators";
import { BaseService } from "src/app/app.base.service";
import { RequestPaymentService } from "src/app/services/RequestBodyService/RequestPayment/request-payment.service";
import { RequestSyndicateService } from "src/app/services/RequestBodyService/RequestSyndicate/request-syndicate.service";
import { StoreJackpotRepositoryService } from "../store/store-jackpot.service";
import { StoreRepositoryService } from "../store/store-repository.service";

@Injectable({
  providedIn: "root",
})
export class PrivateSyndicateRepositoryService extends BaseService {
  syndicateImages$: Observable<any> = new Observable((subscriber) => {
    // Modular Firestore API: Reference the Firestore document
    const syndicateCoverDocRef = doc(this.firestore, `/syndicate_wallpaper/syndicate_cover`);
    // Listen for real-time updates using `onSnapshot`
    const unsubscribe = onSnapshot(
      syndicateCoverDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          subscriber.next(snapshot.data());
        } else {
          subscriber.next(null);
        }
      },
      (error) => {
        console.error("Error fetching syndicate images:", error);
        subscriber.error(error);
      }
    );
    // Cleanup function to unsubscribe from the Firestore listener
    return () => unsubscribe();
  }).pipe(map((res) => res)); // Keep mapping to extract data if required

  getLotteries$ = this.storeService?.lotteries$;
  getJackpots$ = this.storeJackpotService?.jackpots$;
  uploadedImagePath$ = new BehaviorSubject("");

  getSyndicateLotteries$ = combineLatest(this.getLotteries$, this.getJackpots$).pipe(
    filter(([a, b]) => a !== null && b !== null),
    map(([lotteries, jackpots]) => {
      lotteries?.map((lottery: any) => {
        if (!jackpots) return;
        lottery["jackpot"] = jackpots?.find((x: any) => x?.real_name === lottery?.real_name);
      });
      return lotteries;
    })
  );

  syndicateAddOns$: Observable<any> = new Observable((subscriber) => {
    const addOnsCollectionRef = collection(this.firestore, `/addon`);

    const unsubscribe = onSnapshot(
      addOnsCollectionRef,
      (snapshot) => {
        const actions = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })); // Prepare actions with the format expected by snapShotData
        subscriber.next(actions);
      },
      (error) => {
        console.error("Error fetching syndicate addons:", error);
        subscriber.error(error);
      }
    );

    return () => unsubscribe();
  }).pipe(
    map((actions: any[]) => this.snapShotData(actions)), // Pass the properly formatted actions
    shareReplay(1)
  );

  getSyndicateMemberList(syndicateID: number): Observable<any> {
    const moduleName = "get_private_syndicate_members";
    return this.authService.getUid$("PrivateSyndicateRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) return new Observable((subscriber) => subscriber.error("User ID is not found"));

        // Modular Firestore API: Reference the Firestore document
        const syndicateDocRef = doc(this.firestore, `/users/${userId}/syndicate_module/${moduleName}`);

        return this.synchronizerFunction(this.RequestSyndicateService.getSyndicateMembersRequest(syndicateID)).pipe(
          switchMapTo(
            new Observable((subscriber) => {
              // Use `onSnapshot` for real-time updates
              const unsubscribe = onSnapshot(
                syndicateDocRef,
                (snapshot) => {
                  subscriber.next(snapshot.data()); // Emit the document data
                },
                (error) => {
                  console.error("Error fetching syndicate member list:", error);
                  subscriber.error(error);
                }
              );

              // Cleanup function to unsubscribe from Firestore listener
              return () => unsubscribe();
            }).pipe(map((res) => res))
          )
        );
      }));

  }
  constructor(
    private firestore: Firestore,
    private injector: Injector,
    private storeService: StoreRepositoryService,
    private storeJackpotService: StoreJackpotRepositoryService,
    private authService: AuthService,
    private RequestPaymentService: RequestPaymentService,
    private RequestSyndicateService: RequestSyndicateService
  ) {
    super(injector);
  }

  savePrivateSyndicateTickets(syndicateId: string, syndicateData: any) {
    return this.authService.getUid$("PrivateSyndicateRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null);

        const path_id = `rDShdjSvpksqa3To0u0R`;
        const doc_data = syndicateData;
        const dv = { docId: syndicateId };
        const method = "update";

        return this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        }).pipe(take(1));
      })
    );
  }

  getPrivateSyndicateTickets(): Observable<any> {
    return this.authService.getUid$("PrivateSyndicateRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) return new Observable((subscriber) => subscriber.error("User ID not found"));
        // Modular Firestore API: Reference the Firestore collection
        const privateTicketsCollectionRef = collection(this.firestore, `/users/${userId}/private_tickets`);
        // Use `onSnapshot` for real-time updates
        return new Observable((subscriber) => {
          const unsubscribe = onSnapshot(
            privateTicketsCollectionRef,
            (snapshot) => {
              const actions = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              })); // Convert each document into the expected structure
              subscriber.next(actions);
            },
            (error) => {
              console.error("Error fetching private syndicate tickets:", error);
              subscriber.error(error);
            }
          );
          // Cleanup function to unsubscribe from Firestore listener
          return () => unsubscribe();
        }).pipe(
          map((actions: any[]) => this.snapShotData(actions)), // Process the actions via snapShotData
          shareReplay(1)
        );
      }));

  }

  saveLotteryTickets(syndicateId, productGroup) {
    return this.authService?.v2CloudFunctionResponse(
      this.callableFunction(this.requestBodyService.getSaveLotteryTicketRequest(syndicateId, productGroup))
    );
  }

  createPrivateSyndicateOrderId(pArray) {
    return this.authService?.v2CloudFunctionResponse(
      this.callableFunction(this.RequestSyndicateService.getCreateSyndicateOrderIdRequest(pArray))
    );
  }

  getTicketDetailsWithPurchaseId(purchaseId) {
    return this.authService?.v2CloudFunctionResponse(
      this.callableFunction(this.RequestPaymentService.getTicketDetailsWithPurchaseIdRequest(purchaseId))
    );
  }

  createPrivateSyndicate(syndicateData: any) {
    return this.authService?.v2CloudFunctionResponse(
      this.callableFunction(this.RequestSyndicateService.createSyndicateRequest(syndicateData))
    );
  }
  private snapShotData(actions: { id: string; data: () => any }[]): any[] {
    return actions.map((doc) => {
      const data = doc; // Extract document data
      const id = doc.id; // Extract document ID
      return { id, ...data }; // Combine ID and data into the result
    });
  }
}
